import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowUp, FaArrowDown, FaGift, FaUsers, FaTrophy,
  FaCheckCircle, FaClock, FaChevronRight, FaMobileAlt,
  FaCamera, FaUserCheck, FaWallet, FaExclamationTriangle,
} from 'react-icons/fa';
import './PromotionsPage.css';

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const WITHDRAWAL_RULES = [
  { icon: <FaArrowUp />, label: 'Minimum', value: '3 500 FC' },
  { icon: <FaArrowUp />, label: 'Maximum', value: '5 000 000 FC' },
  { icon: <FaClock />,   label: 'Traitement', value: 'Sous 24 heures' },
];

/* ─────────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────────── */
function SectionHeader({ eyebrow, title, desc }) {
  return (
    <div className="pp-section-header">
      {eyebrow && <span className="pp-eyebrow">{eyebrow}</span>}
      <h2 className="pp-section-title">{title}</h2>
      {desc && <p className="pp-section-desc">{desc}</p>}
    </div>
  );
}

function Rule({ icon, text }) {
  return (
    <li className="pp-rule">
      <FaCheckCircle className="pp-rule-icon" />
      <span>{text}</span>
    </li>
  );
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */
export default function PromotionsPage() {
  return (
    <div className="pp-page">

      {/* ── Hero ── */}
      <section className="pp-hero">
        <div className="pp-hero-inner">
          <span className="pp-eyebrow">Promotions & Avantages</span>
          <h1 className="pp-hero-title">
            Plus vous jouez,<br />
            <span className="pp-hero-accent">plus vous gagnez.</span>
          </h1>
          <p className="pp-hero-sub">
            Offre de bienvenue, Champion Contest exclusif —
            invitez 30 amis, prédisez les scores, partagez la cagnotte.
          </p>
          <Link to="/compte" className="pp-hero-cta">
            Accéder à mon compte <FaChevronRight size={12} />
          </Link>
        </div>
      </section>

      {/* ── Comment ça marche ── */}
      <section className="pp-section">
        <SectionHeader
          eyebrow="Guide d'utilisation"
          title="Comment ça marche ?"
          desc="Tout ce que vous devez savoir pour déposer, parier et retirer vos gains sur AFRIBET."
        />

        <div className="pp-howto-grid">

          {/* ── Dépôt ── */}
          <div className="pp-howto-card">
            <div className="pp-howto-card-header pp-howto-deposit">
              <div className="pp-howto-icon"><FaArrowDown /></div>
              <h3 className="pp-howto-title">Faire un dépôt</h3>
            </div>
            <div className="pp-steps">
              <div className="pp-step">
                <div className="pp-step-num">1</div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Allez dans votre compte</p>
                  <p className="pp-step-desc">Connectez-vous et accédez à votre tableau de bord. Cliquez sur <strong>Dépôt</strong>.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num"><FaMobileAlt /></div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Transférez via Orange ou Airtel</p>
                  <p className="pp-step-desc">Vous verrez un numéro Orange Money ou Airtel Money. Faites le transfert directement depuis votre application mobile money.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num"><FaCamera /></div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Envoyez la capture d'écran</p>
                  <p className="pp-step-desc">Prenez une capture d'écran de la confirmation du transfert et téléchargez-la dans le formulaire de dépôt.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num"><FaUserCheck /></div>
                <div className="pp-step-body">
                  <p className="pp-step-title">L'admin valide votre dépôt</p>
                  <p className="pp-step-desc">Une fois la preuve vérifiée, l'admin approuve et votre solde est crédité automatiquement. Vous pouvez alors parier !</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num pp-step-num-alert"><FaExclamationTriangle /></div>
                <div className="pp-step-body">
                  <p className="pp-step-title pp-step-title-alert">Si le dépôt est rejeté</p>
                  <p className="pp-step-desc">Vous recevez une <strong>notification</strong> expliquant pourquoi la photo a été rejetée (floue, mauvais transfert, etc.) pour que vous puissiez soumettre à nouveau.</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Retrait ── */}
          <div className="pp-howto-card">
            <div className="pp-howto-card-header pp-howto-withdraw">
              <div className="pp-howto-icon"><FaArrowUp /></div>
              <h3 className="pp-howto-title">Faire un retrait</h3>
            </div>
            <div className="pp-steps">
              <div className="pp-step">
                <div className="pp-step-num">1</div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Allez dans votre compte</p>
                  <p className="pp-step-desc">Accédez à votre tableau de bord et cliquez sur <strong>Retrait</strong>.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num">2</div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Entrez votre numéro et le montant</p>
                  <p className="pp-step-desc">Indiquez votre numéro Mobile Money (Orange ou Airtel) et le montant que vous souhaitez retirer.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step">
                <div className="pp-step-num">3</div>
                <div className="pp-step-body">
                  <p className="pp-step-title">Soumettez votre demande</p>
                  <p className="pp-step-desc">Envoyez la requête. L'admin traite les retraits sous 24 heures.</p>
                </div>
              </div>
              <div className="pp-step-connector" />
              <div className="pp-step pp-step-reward">
                <div className="pp-step-num pp-step-num-gold"><FaWallet /></div>
                <div className="pp-step-body">
                  <p className="pp-step-title">L'admin vous envoie l'argent</p>
                  <p className="pp-step-desc">L'admin effectue le transfert Mobile Money vers votre numéro. Vous verrez la confirmation dans votre <strong>historique de retraits</strong>.</p>
                </div>
              </div>
            </div>

            {/* Key info */}
            <div className="pp-rules-box" style={{ marginTop: 20 }}>
              <p className="pp-rules-title">À savoir</p>
              <ul className="pp-rules-list">
                {/* <Rule text="Retrait minimum : 3 500 FC — maximum : 5 000 000 FC." /> */}
                <Rule text="Une seule demande de retrait en attente à la fois." />
                <Rule text="Traitement sous 24 heures ouvrables." />
                <Rule text="Notifications activées pour suivre le statut en temps réel." />
              </ul>
            </div>
          </div>

        </div>

        {/* Quick tip */}
        {/* <div className="pp-howto-tip">
          <FaBell className="pp-tip-icon" />
          <p>
            Activez les notifications pour être informé en temps réel de l'approbation ou du rejet de vos dépôts et retraits.
          </p>
          <Link to="/compte" className="pp-cta-btn">
            Mon compte <FaChevronRight size={11} />
          </Link>
        </div> */}
      </section>

      {/* ── Retraits ── */}
      <section className="pp-section">
        <SectionHeader
          eyebrow="Portefeuille"
          title="Conditions de retrait"
          desc="Retirez vos gains rapidement et en toute sécurité."
        />

        <div className="pp-withdraw-grid">
          {WITHDRAWAL_RULES.map(({ icon, label, value }) => (
            <div className="pp-withdraw-card" key={label}>
              <div className="pp-wc-icon">{icon}</div>
              <div>
                <p className="pp-wc-label">{label}</p>
                <p className="pp-wc-value">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Offre bienvenue ── */}
      <section className="pp-section">
        <SectionHeader
          eyebrow="Offre de bienvenue"
          title="Dépose & Rejoue"
          desc="Réservé aux nouveaux membres. Perdez votre dépôt le jour J ? On vous le rembourse."
        />

        <div className="pp-welcome-card">
          {/* Badge */}
          <div className="pp-welcome-badge">
            <FaGift />
            <span>Nouveau compte</span>
          </div>

          {/* Steps */}
          <div className="pp-steps">
            <div className="pp-step">
              <div className="pp-step-num">1</div>
              <div className="pp-step-body">
                <p className="pp-step-title">Ouvrez votre compte</p>
                <p className="pp-step-desc">Inscrivez-vous gratuitement sur AFRIBET.</p>
              </div>
            </div>
            <div className="pp-step-connector" />
            <div className="pp-step">
              <div className="pp-step-num">2</div>
              <div className="pp-step-body">
                <p className="pp-step-title">Déposez minimum 10 000 FC</p>
                <p className="pp-step-desc">Le dépôt doit être effectué le jour de votre inscription.</p>
              </div>
            </div>
            <div className="pp-step-connector" />
            <div className="pp-step">
              <div className="pp-step-num">3</div>
              <div className="pp-step-body">
                <p className="pp-step-title">Pariez dans les 24 heures</p>
                <p className="pp-step-desc">Si vous perdez la totalité de votre dépôt dans ce délai…</p>
              </div>
            </div>
            <div className="pp-step-connector" />
            <div className="pp-step pp-step-reward">
              {/* <div className="pp-step-num pp-step-num-gold">🎁</div>
              <div className="pp-step-body">
                <p className="pp-step-title">AFRIBET vous rembourse !</p>
                <p className="pp-step-desc">
                  Vous recevez un bonus égal à votre dépôt initial —
                  jusqu'à <strong>10 000 FC</strong> offerts pour recommencer.
                </p>
              </div> */}
            </div>
          </div>

          {/* Rules */}
          <div className="pp-rules-box">
            <p className="pp-rules-title">Conditions</p>
            <ul className="pp-rules-list">
              <Rule text="Nouveau compte uniquement — une seule fois par joueur." />
              <Rule text="Dépôt minimum de 10 000 FC le jour de l'inscription." />
              {/* <Rule text="La perte totale doit survenir dans les 24 heures suivant le dépôt." />
              <Rule text="Le bonus est crédité automatiquement sur votre solde." /> */}
            </ul>
          </div>

          <Link to="/register" className="pp-cta-btn">
            Créer mon compte gratuitement <FaChevronRight size={11} />
          </Link>
        </div>
      </section>

      {/* ── Programme d'affiliation + Champion Contest ── */}
      <section className="pp-section">
        <SectionHeader
          eyebrow="Programme d'affiliation & Champion Contest"
          title="Invitez, débloquez, gagnez la cagnotte"
          desc="Un programme en deux temps : invitez vos amis pour débloquer le concours, puis prédisez les scores pour remporter la mise."
        />

        {/* Step 1 — Invite */}
        <div className="pp-welcome-card" style={{ marginBottom: 16 }}>
          <div className="pp-welcome-badge">
            <FaUsers />
            <span>Étape 1 — Parrainer</span>
          </div>

          <div className="pp-steps">
            <div className="pp-step">
              <div className="pp-step-num">1</div>
              <div className="pp-step-body">
                <p className="pp-step-title">Partagez votre lien ou code</p>
                <p className="pp-step-desc">
                  Disponible dans votre espace "Mon compte → Parrainage". Envoyez-le à vos amis par WhatsApp, SMS ou réseaux sociaux.
                </p>
              </div>
            </div>
            <div className="pp-step-connector" />
            <div className="pp-step">
              <div className="pp-step-num">2</div>
              <div className="pp-step-body">
                <p className="pp-step-title">Vos amis s'inscrivent</p>
                <p className="pp-step-desc">
                  Chaque ami qui crée son compte avec votre lien ou code est comptabilisé automatiquement comme filleul.
                </p>
              </div>
            </div>
            <div className="pp-step-connector" />
            <div className="pp-step pp-step-reward">
              <div className="pp-step-num pp-step-num-gold">🏆</div>
              <div className="pp-step-body">
                <p className="pp-step-title">30 filleuls = Champion Contest débloqué !</p>
                <p className="pp-step-desc">
                  Dès que vous atteignez <strong>30 filleuls</strong>, le Champion Contest s'ouvre automatiquement pour vous — sans aucune autre condition.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2 — Champion Contest */}
        <div className="pp-champion-card">
          <div className="pp-champion-badge">
            <FaTrophy /> Champion Contest
          </div>
          <h3 className="pp-champion-title">Comment fonctionne le Champion Contest ?</h3>
          <p className="pp-champion-desc">
            Une fois débloqué, vous accédez à des concours exclusifs où vous prédisez les scores de matchs réels pour gagner une cagnotte partagée.
          </p>

          <div className="pp-champion-features">
            <div className="pp-cf-item">
              <FaCheckCircle /> Les matchs sont sélectionnés par l'admin
            </div>
            <div className="pp-cf-item">
              <FaCheckCircle /> Vous prédisez le score de chaque match (ex : 1-0, 2-1…)
            </div>
            <div className="pp-cf-item">
              <FaCheckCircle /> Tous les participants qualifiés (30+ filleuls) jouent ensemble
            </div>
            <div className="pp-cf-item">
              <FaCheckCircle /> Les gagnants se partagent la cagnotte équitablement
            </div>
          </div>

          {/* Prize sharing example */}
          <div className="pp-rules-box" style={{ marginBottom: 24 }}>
            <p className="pp-rules-title">Exemple concret</p>
            <ul className="pp-rules-list">
              <Rule text="L'admin crée un concours avec 6 matchs et une cagnotte " />
              <Rule text="10 membres qualifiés participent et soumettent leurs prédictions." />
              <Rule text="Après les matchs, 3 membres ont tous leurs scores corrects." />
              <Rule text="Ces 3 gagnants se partagent la cagnotte chacun." />
              <Rule text="Si une seule personne gagne, elle emporte toute la cagnotte." />
            </ul>
          </div>

          <div className="pp-champion-cta-row">
            <div className="pp-champion-unlock">
              <span className="pp-cu-label">Condition d'accès</span>
              <span className="pp-cu-val">30 filleuls</span>
            </div>
            <Link to="/championnat" className="pp-cta-btn pp-cta-gold">
              Voir mon avancement <FaChevronRight size={11} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA bottom ── */}
      {/* <section className="pp-bottom-cta">
        <h2>Prêt à commencer ?</h2>
        <p>Créez votre compte gratuitement et profitez de tous les avantages AFRIBET dès aujourd'hui.</p>
        <div className="pp-bottom-btns">
          <Link to="/register" className="pp-cta-btn">S'inscrire gratuitement</Link>
          <Link to="/" className="pp-cta-ghost">Voir les matchs →</Link>
        </div>
      </section> */}

    </div>
  );
}
